import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { useForgotPassword } from '@/features/auth/hooks/useAuthMutations';
import { AuthCardLayout } from './../components/AuthCardLayout';
import { AuthForm } from './../components/AuthForm';

/**
 * ForgotPasswordPage — privacy-preserving email submission.
 *
 * The API always returns 200 on this endpoint regardless of whether
 * the email belongs to a real user (a deliberate anti-enumeration
 * measure). The UI surfaces the same generic message either way,
 * matching the server contract.
 */
const forgotSchema = z.object({
  email: z.string().email('Ingresa un correo válido'),
});

type ForgotValues = { email: string };

const FORGOT_FIELDS = [
  {
    name: 'email' as const,
    def: {
      name: 'email',
      label: 'Correo electrónico',
      type: 'email' as const,
      autoComplete: 'email',
      required: true,
      inputMode: 'email' as const,
      placeholder: 'usuario@empresa.com',
    },
  },
];

export function ForgotPasswordPage(): ReactElement {
  const forgot = useForgotPassword();

  return (
    <AuthCardLayout
      title="Restablecer contraseña"
      subtitle="Te enviaremos instrucciones para crear una nueva"
      footer={
        <span>
          ¿Ya la recordás?{' '}
          <Link
            to="/login"
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Volver a iniciar sesión
          </Link>
        </span>
      }
    >
      <AuthForm<ForgotValues>
        schema={forgotSchema}
        fields={FORGOT_FIELDS}
        defaults={{ email: '' }}
        submitting={forgot.isPending}
        submitLabel="Enviar enlace"
        onValidSubmit={async (values) => {
          await forgot.mutateAsync(values);
        }}
      />
    </AuthCardLayout>
  );
}
